import { useMemo } from "react";
import { useGetIdentity } from "ra-core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import type { ThreadNote, ThreadResource } from "@/modules/threads/threadModel";

export const QUICK_REACTIONS = ["👍", "✅", "👀", "🎉", "❤️", "😂"] as const;

type ReactionRow = {
  id: number;
  note_resource: ThreadResource;
  note_id: number;
  member_id: number;
  emoji: string;
};

export type ReactionSummary = {
  emoji: string;
  count: number;
  mine: boolean;
  memberIds: number[];
};

const KEY = "note_reactions";

/** Reactions of the notes in view, grouped per note and emoji. */
export const useNoteReactions = (notes: ThreadNote[]) => {
  const { identity } = useGetIdentity();
  const ids = useMemo(() => {
    const byResource: Record<ThreadResource, number[]> = {
      contact_notes: [],
      deal_notes: [],
    };
    for (const note of notes) byResource[note.resource].push(Number(note.id));
    return byResource;
  }, [notes]);

  const { data = [] } = useQuery({
    queryKey: [KEY, ids],
    enabled: ids.contact_notes.length + ids.deal_notes.length > 0,
    staleTime: 15_000,
    queryFn: async () => {
      const rows: ReactionRow[] = [];
      for (const resource of ["contact_notes", "deal_notes"] as const) {
        if (!ids[resource].length) continue;
        const { data: chunk } = await supabase
          .from("note_reactions")
          .select("id, note_resource, note_id, member_id, emoji")
          .eq("note_resource", resource)
          .in("note_id", ids[resource]);
        rows.push(...((chunk ?? []) as ReactionRow[]));
      }
      return rows;
    },
  });

  return useMemo(() => {
    const byNote = new Map<string, ReactionSummary[]>();
    for (const row of data) {
      const key = `${row.note_resource}:${row.note_id}`;
      const list = byNote.get(key) ?? [];
      let entry = list.find((item) => item.emoji === row.emoji);
      if (!entry) {
        entry = { emoji: row.emoji, count: 0, mine: false, memberIds: [] };
        list.push(entry);
      }
      entry.count += 1;
      entry.memberIds.push(row.member_id);
      if (String(row.member_id) === String(identity?.id)) entry.mine = true;
      byNote.set(key, list);
    }
    return byNote;
  }, [data, identity?.id]);
};

/** Add or remove my reaction. */
export const useToggleReaction = () => {
  const { identity } = useGetIdentity();
  const queryClient = useQueryClient();
  return async (note: ThreadNote, emoji: string, mine: boolean) => {
    const memberId = identity?.id;
    const orgId = (identity as { org_id?: number } | undefined)?.org_id;
    if (memberId == null || orgId == null) return;
    if (mine) {
      await supabase
        .from("note_reactions")
        .delete()
        .eq("note_resource", note.resource)
        .eq("note_id", note.id)
        .eq("member_id", memberId)
        .eq("emoji", emoji);
    } else {
      await supabase.from("note_reactions").upsert(
        {
          org_id: orgId,
          note_resource: note.resource,
          note_id: note.id,
          member_id: memberId,
          emoji,
        },
        {
          onConflict: "note_resource,note_id,member_id,emoji",
          ignoreDuplicates: true,
        },
      );
    }
    await queryClient.invalidateQueries({ queryKey: [KEY] });
  };
};
